<!--
  Chip z nazwą grupy kontaktów i sumarycznymi licznikami kanałów
  (e-mail / SMS / MMS). Tylko prezentacja — bez interakcji.

  Render jako <span>, żeby uniknąć globalnych reguł z `button-system.css`
  (min-width 132px / min-height 38px), które rozjeżdżają chipy.
-->
<template>
  <span
    class="cg-chip"
    :title="ariaTitle"
    :aria-label="ariaTitle"
  >
    <span class="cg-chip__name">{{ group.name }}</span>
    <span class="cg-chip__counts" aria-hidden="true">
      <span class="cg-chip__count" :title="t('recipients-people')">
        {{ summary.count }} {{ t('recipients-short') }}
      </span>
      <span v-if="summary.emails > 0" class="cg-chip__count cg-chip__count--email">
        {{ summary.emails }} e-mail
      </span>
      <span v-if="summary.sms > 0" class="cg-chip__count cg-chip__count--sms">
        {{ summary.sms }} SMS
      </span>
      <span v-if="summary.mms > 0" class="cg-chip__count cg-chip__count--mms">
        {{ summary.mms }} MMS
      </span>
    </span>
  </span>
</template>

<script setup>
import { computed, getCurrentInstance } from 'vue';

const props = defineProps({
  group: {
    type: Object,
    required: true,
  },
  // Akceptowane dla zgodności z miejscami przekazującymi `:clickable="false"`,
  // ale chip nigdy nie jest klikalny — modal został usunięty.
  clickable: {
    type: Boolean,
    default: false,
  },
});

const t = getCurrentInstance()?.appContext.config.globalProperties.t ?? (k => k);

const summary = computed(() => props.group.contactsSummary ?? { count: 0, emails: 0, sms: 0, mms: 0 });

const ariaTitle = computed(() => {
  const s = summary.value;
  return `${props.group.name} — ${s.count} ${t('recipients-people')} (${s.emails} e-mail, ${s.sms} SMS, ${s.mms} MMS)`;
});
</script>

<style scoped>
.cg-chip {
  display: inline-flex;
  align-items: center;
  gap: 0.45rem;
  padding: 0.25rem 0.4rem 0.25rem 0.75rem;
  border: 1px solid var(--border-default);
  background-color: var(--surface-primary);
  color: var(--text-primary);
  border-radius: 999px;
  font-size: 0.82rem;
  line-height: 1.4;
  white-space: nowrap;
  user-select: none;
  box-shadow: 0 1px 0 rgba(15, 23, 42, 0.03);
}

.cg-chip__name {
  font-weight: var(--font-weight-medium);
  letter-spacing: 0.01em;
}
.cg-chip__counts {
  display: inline-flex;
  gap: 0.2rem;
  color: var(--text-secondary);
  font-variant-numeric: tabular-nums;
}
.cg-chip__count {
  display: inline-flex;
  align-items: center;
  padding: 0.05rem 0.5rem;
  border-radius: 999px;
  background-color: var(--surface-secondary);
  border: 1px solid var(--border-default);
  font-size: 0.75rem;
  font-weight: var(--font-weight-medium);
}
.cg-chip__count--email { color: var(--accent-blue); background-color: var(--accent-blue-soft); border-color: var(--accent-blue-border); }
.cg-chip__count--sms   { color: var(--status-ok); background-color: var(--status-ok-soft); border-color: var(--status-ok-border); }
.cg-chip__count--mms   { color: var(--status-warn-strong); background-color: var(--status-warn-soft); border-color: var(--status-warn-border); }
</style>
